"use client";

import { useEffect, useState } from "react";
import * as Sentry from "@sentry/nextjs";
import { Button } from "@/components/ui/button";
import { AlertCircle, RefreshCw, Home } from "lucide-react";
import Link from "next/link";

interface ErrorProps {
  error: Error & { digest?: string };
  reset: () => void;
}

export default function Error({ error, reset }: ErrorProps) {
  // ⚠️ NEDEN OLAY KİMLİĞİ SAKLANIYOR: bu ekran production'da HİÇBİR tanımlayıcı
  // göstermiyordu (mesaj da `digest` de yalnız development'ta basılıyordu). Sonuç:
  // kullanıcı "hata aldım" diyor, elinde tek bir kod bile olmuyor ve Sentry'deki
  // binlerce olay arasında hangisi olduğu bulunamıyor. 2026-09-07'de tam bu yüzden
  // "Bir Şeyler Yanlış Gitti" şikâyeti günlerce teşhis edilemedi.
  // `captureException` olayın kimliğini DÖNDÜRÜYOR; Sentry aramasına doğrudan
  // yapıştırılabilen tek değer bu.
  // ⚠️ NEDEN `useState` BAŞLATICISI, `useEffect` DEĞİL: kimliği efekt içinde
  // `setState` ile yazmak basamaklı render üretir ve ESLint (react-hooks/
  // set-state-in-effect) bunu reddediyor. Tembel başlatıcı ise mount'ta BİR KEZ
  // çalışır ve değeri ilk render'da hazır eder — kullanıcı kodu anında görür.
  const [olayId] = useState<string | null>(() => Sentry.captureException(error) ?? null);

  useEffect(() => {
    console.error("Bir hata olustu:", error);
  }, [error]);

  // `digest` sunucu tarafı hatalarında, olay kimliği her ikisinde de dolu olur.
  const kod = olayId ?? error.digest ?? null;

  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center px-4 py-16">
      <div className="mx-auto max-w-md text-center">
        {/* Hata İkonu */}
        <div className="mb-6 flex justify-center">
          <div className="bg-destructive/10 rounded-full p-4">
            <AlertCircle className="text-destructive h-12 w-12" />
          </div>
        </div>

        {/* Başlık */}
        <h1 className="text-foreground mb-2 text-2xl font-bold">Bir Şeyler Yanlış Gitti</h1>

        {/* Açıklama */}
        <p className="text-muted-foreground mb-6">
          Üzgünüz, beklenmeyen bir hata oluştu. Lütfen sayfayı yenilemeyi deneyin veya ana sayfaya
          dönün.
        </p>

        {/* Hata metninin TAMAMI yalnız development'ta — production'da iç ayrıntı
            sızdırmamak için. */}
        {process.env.NODE_ENV === "development" && error.message && (
          <div className="bg-muted mb-6 rounded-lg p-4 text-left">
            <p className="text-muted-foreground font-mono text-sm">{error.message}</p>
          </div>
        )}

        {/* Hata kodu HER ORTAMDA. Sızıntı değil: olay kimliği rastgele bir UUID,
            `digest` ise bir özet — ikisi de içerik taşımaz, yalnız kaydı işaret eder. */}
        {kod && (
          <p className="text-muted-foreground mb-6 font-mono text-xs break-all">Hata kodu: {kod}</p>
        )}

        {/* Aksiyonlar */}
        <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
          <Button onClick={reset} variant="default" className="gap-2">
            <RefreshCw className="h-4 w-4" />
            Tekrar Dene
          </Button>
          <Button asChild variant="outline" className="gap-2">
            <Link href="/">
              <Home className="h-4 w-4" />
              Ana Sayfaya Dön
            </Link>
          </Button>
        </div>

        {/* Destek Linki */}
        <p className="text-muted-foreground mt-8 text-sm">
          Sorun devam ederse{" "}
          <Link href="/about#iletisim" className="text-primary hover:underline">
            bizimle iletişime geçin
          </Link>
          .
        </p>
      </div>
    </div>
  );
}
